import { createSlice } from "@reduxjs/toolkit"
import { CampaignDetail } from "types/campaign"
import { CampaignApplication } from "types/campaign/applications"
import { SocialPost } from "types/campaign/collaborations"
import { CompetitionGroup } from "types/competition-analysis"
import { Influencer, SocialAccount } from "types/influencer"

export interface CampaignInviteCreatorPreview {
    uuid: string
    name: string
    handle: string
    profile_picture_url: string | null
    platform?: string
    followers_count?: number
}

export interface UIComponents {
    influencerPostModal: {
        postId: string,
        collaborationId?: string,
        influencer?: Influencer,
        socialAccount: SocialAccount,
        status?: 'pending' | 'approved' | 'rejected' | 'processing',
        milestoneStep?: string | null,
        preventAction?: boolean
    } | null,
    socialPostModal: {
        postId: string,
        socialAccount: SocialAccount,
        influencer?: Influencer,
        showPermalink?: boolean
    } | null,
    paymentsOffcanvas: boolean,
    applicationOffcanvas: {
        campaignId: string,
        influencerId?: string,
        socialAccountId?: string,
        status: 'pending' | 'accepted' | 'rejected' | 'processing'
        approve?: () => void,
        reject?: () => void
    } | null,
    applicationView: {
        applicationId?: string,
        campaignId?: string,
        socialAccountId: string,
        showPermalink?: boolean
    } | null,
    postPerformanceOffcanvas: {
        collaborationId: string,
    } | null,
    error: {
        code: number,
        message: string
    } | null,
    header: {
        campaignData?: CampaignDetail | null,
        detailPage?: boolean,
        competitionGroup?: CompetitionGroup | null
    } | null,
    lastActiveCampaignTab: 'applications' | 'shortlisted' | 'collaborations' | 'insights' | 'recommended-creators' | 'invites',
    campaignsLocation: {pathname: string, search?: string},
    notificationPreferences: boolean,
    approveApplicationCostModal: {
        application: CampaignApplication;
        isOpen: boolean;
    } | null,
    addSocialAccountOffcanvas: {
        isActive: boolean
    },
    addGSTModalVisibility: boolean,
    manageCollectionModal: {
        post: SocialPost | null,
        creator: SocialAccount | null,
        itemType: 'post' | 'account',
        addMode: boolean,
        currentCollectionId?: string
    } | null,
    limitExceededModal: string | null,
    shortlistExitAnimation: {
        campaignId: string,
        applicationId: string,
    } | null,
    shortlistTabPulse: number,
    campaignInviteModal: {
        socialAccountIds: string[],
        creators?: CampaignInviteCreatorPreview[],
        mode?: string,
    } | null,
}

const initialState: UIComponents = {
    influencerPostModal : null,
    paymentsOffcanvas: false,
    error: null,
    header: {
        campaignData: null,
        detailPage: false
    },
    applicationOffcanvas: null,
    applicationView: null,
    postPerformanceOffcanvas: null,
    lastActiveCampaignTab: 'applications',
    campaignsLocation: {pathname: '/campaign'},
    notificationPreferences: false,
    approveApplicationCostModal: null,
    socialPostModal: null,
    addSocialAccountOffcanvas: {
        isActive: false
    },
    addGSTModalVisibility: false,
    manageCollectionModal: null,
    limitExceededModal: null,
    shortlistExitAnimation: null,
    shortlistTabPulse: 0,
    campaignInviteModal: null,
}

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        showInfluencerPostModal: (state: UIComponents, action: { payload: { postId: string, collaborationId?: string, influencer?: Influencer, socialAccount: SocialAccount, status?: 'pending' | 'approved' | 'rejected' | 'processing', milestoneStep?: string | null, preventAction?: boolean } }) => {
                state.influencerPostModal = action.payload;
            },
        closeInfluencerPostModal: (state: UIComponents) => {
            state.influencerPostModal = null;
        },
        showSocialPostModal: (state: UIComponents, action: { payload: { postId: string, influencer?: Influencer, socialAccount: SocialAccount, showPermalink?: boolean } }) => {
                state.socialPostModal = action.payload;
            },
        closeSocialPostModal: (state: UIComponents) => {
            state.socialPostModal = null;
        },
        togglePaymentsOffcanvas: (state: UIComponents, action : {payload: boolean}) => {
            state.paymentsOffcanvas = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setHeaderCampaignData: (state, {payload}) => {
            const {campaign, detailPage} = payload;
            state.header = {
                ...(state.header ?? {}),
                campaignData: campaign,
                detailPage
            }
        },
        setHeaderCompetitionGroup: (state, {payload}) => {
            const {competitionGroup} = payload;
            state.header = {
                ...(state.header ?? {}),
                competitionGroup
            }
        },
        toggleApplicationOffcanvas: (state, action : {payload: {campaignId: string, influencerId?: string, socialAccountId: string, status: 'pending' | 'accepted' | 'rejected' | 'processing', approve?: () => void, reject?: () => void} | null}) => {
            state.applicationOffcanvas = action.payload;
        },
        togglePostPerformanceOffcanvas: (state, action: {payload: {collaborationId: string} | null}) => {
            state.postPerformanceOffcanvas = action.payload
        },
        setLastActiveCampaignTab : (state, action: {payload: 'applications' | 'shortlisted' | 'collaborations' | 'insights' | 'recommended-creators' | 'invites'}) => {
            state.lastActiveCampaignTab = action.payload;
        }, 
        setCampaignsLocation: (state, action: {payload: {pathname: string, search?: string}}) => {
            state.campaignsLocation = action.payload
        },
        toggleNotificationPreferencesModal: (state, {payload}) => {
            state.notificationPreferences = payload;
        },
        toggleApproveApplicationCostModal: (state, {payload}: {payload: {application: CampaignApplication; isOpen: boolean} | null}) => {
            state.approveApplicationCostModal = payload;
        },
        toggleAddSocialAccountOffcanvas: (state, {payload}) => {
            state.addSocialAccountOffcanvas.isActive = payload;
        },
        toggleAddGSTModalVisibility: (state, {payload}) => {
            state.addGSTModalVisibility = payload;
        },
        toggleManageCollectionModal: (state, {payload}: {payload: {post: SocialPost | null, creator: SocialAccount | null, itemType: 'post' | 'account', addMode:boolean, currentCollectionId?: string} | null}) => {
            state.manageCollectionModal = payload;
        },
        toggleLimitExceededModal: (state, {payload}: {payload: string | null}) => {
            state.limitExceededModal = payload;
        },
        toggleApplicationView: (state, {payload}: {payload: {applicationId?: string, campaignId?: string, socialAccountId: string, showPermalink?: boolean} | null}) => {
            state.applicationView = payload;
        },
        startShortlistExit: (
            state,
            { payload }: { payload: { campaignId: string; applicationId: string } }
        ) => {
            state.shortlistExitAnimation = payload;
        },
        clearShortlistExit: (state) => {
            state.shortlistExitAnimation = null;
        },
        pulseShortlistTab: (state) => {
            state.shortlistTabPulse += 1;
        },
        showCampaignInviteModal: (
            state,
            { payload }: { payload: { socialAccountIds: string[]; creators?: CampaignInviteCreatorPreview[]; mode?: string } }
        ) => {
            state.campaignInviteModal = payload;
        },
        closeCampaignInviteModal: (state) => {
            state.campaignInviteModal = null;
        },
    }
})

export const {
    showInfluencerPostModal,
    closeInfluencerPostModal,
    showSocialPostModal,
    closeSocialPostModal,
    togglePaymentsOffcanvas,
    setError,
    setHeaderCampaignData,
    toggleApplicationOffcanvas,
    togglePostPerformanceOffcanvas,
    setCampaignsLocation,
    setLastActiveCampaignTab,
    toggleNotificationPreferencesModal,
    toggleApproveApplicationCostModal,
    toggleAddSocialAccountOffcanvas,
    setHeaderCompetitionGroup,
    toggleAddGSTModalVisibility,
    toggleManageCollectionModal,
    toggleLimitExceededModal,
    toggleApplicationView,
    startShortlistExit,
    clearShortlistExit,
    pulseShortlistTab,
    showCampaignInviteModal,
    closeCampaignInviteModal,
} = uiSlice.actions;

export default uiSlice.reducer